import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { apiFetch } from "@/auth/apiClient";
import { formatApiError } from "@/api/userAccount";
import { BrandLogo } from "@/components/BrandLogo";
import { ArrowLeft, KeyRound, Mail, ShieldAlert } from "lucide-react";

type Step = "email" | "otp" | "password";

const slideVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: -20, transition: { duration: 0.3 } }
};

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address.");
      return;
    }
    setLoading(true);
    try {
      await apiFetch("/auth/forgot_password", {
        method: "POST",
        json: { email: email.trim() }
      });
      toast.success("Verification code sent to your email!");
      setStep("otp");
    } catch (err: unknown) {
      toast.error(formatApiError(err) || "Failed to send verification code.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length < 6) {
      toast.error("Please enter the complete 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch<{ accessToken: string }>("/auth/verify_otp", {
        method: "POST",
        json: { email: email.trim(), otp: Number(otp) }
      });
      toast.success("Code verified successfully!");
      setResetToken(res.accessToken);
      setStep("password");
    } catch (err: unknown) {
      toast.error(formatApiError(err) || "Invalid or expired verification code.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await apiFetch("/auth/forgot_password", {
        method: "POST",
        json: { email: email.trim() }
      });
      toast.success("A new verification code has been sent!");
      setOtp("");
    } catch (err: unknown) {
      toast.error(formatApiError(err) || "Failed to resend code.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters long.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("New passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await apiFetch("/auth/set_password", {
        method: "POST",
        json: { password },
        headers: {
          Authorization: `Bearer ${resetToken}`
        }
      });
      toast.success("Password reset successfully! Please sign in with your new password.");
      navigate("/login");
    } catch (err: unknown) {
      toast.error(formatApiError(err) || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/5 via-background to-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="flex justify-center">
            <BrandLogo asLinkTo="/" size="xl" showText={false} />
          </div>
          <h1 className="text-2xl font-bold text-foreground mt-4">Reset Password</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Follow the steps to recover your account access.
          </p>
        </div>

        <KCard className="p-6 overflow-hidden">
          <AnimatePresence mode="wait">
            {step === "email" && (
              <motion.div
                key="email-step"
                variants={slideVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="space-y-4"
              >
                <div className="flex items-center space-x-2 text-primary">
                  <Mail className="h-5 w-5" />
                  <h2 className="text-lg font-semibold">Enter your email</h2>
                </div>
                <p className="text-xs text-muted-foreground">
                  We'll send a 6-digit confirmation code to this address if it matches an active account.
                </p>

                <form onSubmit={handleRequestOtp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      disabled={loading}
                    />
                  </div>
                  <Button className="w-full" type="submit" disabled={loading}>
                    {loading ? "Sending Code..." : "Send Verification Code"}
                  </Button>
                </form>
              </motion.div>
            )}

            {step === "otp" && (
              <motion.div
                key="otp-step"
                variants={slideVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="space-y-4"
              >
                <div className="flex items-center space-x-2 text-primary">
                  <KeyRound className="h-5 w-5" />
                  <h2 className="text-lg font-semibold">Verify OTP Code</h2>
                </div>
                <p className="text-xs text-muted-foreground">
                  A verification code was sent to <span className="font-semibold text-foreground">{email}</span>. Enter it below to proceed.
                </p>

                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <div className="space-y-2 flex flex-col items-center">
                    <Label htmlFor="otp" className="self-start">Verification Code</Label>
                    <InputOTP
                      maxLength={6}
                      value={otp}
                      onChange={setOtp}
                      disabled={loading}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>

                  <div className="flex items-center justify-between text-xs pt-1">
                    <button
                      type="button"
                      onClick={() => setStep("email")}
                      className="text-muted-foreground hover:text-foreground flex items-center hover:underline"
                      disabled={loading}
                    >
                      <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Change Email
                    </button>
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      className="text-primary hover:underline font-medium"
                      disabled={loading}
                    >
                      Resend Code
                    </button>
                  </div>

                  <Button className="w-full" type="submit" disabled={loading || otp.length < 6}>
                    {loading ? "Verifying..." : "Verify Code"}
                  </Button>
                </form>
              </motion.div>
            )}

            {step === "password" && (
              <motion.div
                key="password-step"
                variants={slideVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="space-y-4"
              >
                <div className="flex items-center space-x-2 text-primary">
                  <ShieldAlert className="h-5 w-5" />
                  <h2 className="text-lg font-semibold">Choose New Password</h2>
                </div>
                <p className="text-xs text-muted-foreground">
                  Your identity has been verified. Choose a strong, new password of at least 8 characters.
                </p>

                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">New Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={8}
                      disabled={loading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      disabled={loading}
                    />
                  </div>
                  <Button className="w-full" type="submit" disabled={loading}>
                    {loading ? "Resetting Password..." : "Update Password"}
                  </Button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </KCard>

        <p className="text-sm text-muted-foreground mt-4 text-center">
          Remembered your password?{" "}
          <Link to="/login" className="text-primary font-semibold hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
